import { useEffect, useState } from "react";
import { useExclusiveSubmit } from "../custom-hooks/useExclusiveSubmit";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  TransitionChild,
} from "@headlessui/react";

import {
  BookmarkIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { BookmarkIcon as FilledBookmark } from "@heroicons/react/24/solid";

import { useDispatch, useSelector } from "react-redux";

import { setDrawer } from "../redux-store/features/layoutSlice";

import CustomButton from "./CustomButton";

import CustomBadges from "./CustomBadges";

import Capabilities from "./Capabilities";
import ScopeWithAdditionalTags from "./ScopeWithAdditionalTags";

import {
  useDocumentFile,
  useGetPurchaseVendorBySponsor,
  useGetRequestBookmarkDetail,
  usePostDocumentsBookmarks,
  usePostRequestAccess,
  useSendRfiToPurchaseVendor,
  useGetDocumentActivity,
} from "../custom-hooks/queries";

import Notification from "./Notification";

import {
  dateFormat,
  isDiligentAdminUserRole,
  useUserRole,
} from "../utils/helperMethods";
import { canRequestLibraryDocAccess } from "../utils/rfiRequestActionPermissions";
import { canRequestLibraryDocument, useIsOrgVqaOnly } from "../utils/vqaOnlyOrg";

import { useNavigate } from "react-router-dom";

import CustomConfirmModal from "./CustomConfirmModal";

import Modal from "./Modal";

import { StatusBadges } from "./StatusBadges";

import StatusForDrawer from "./StatusForDrawer";

import DrawerSkeleton from "./Skeletons/drawerLoadingSkeleton";

import { QueryClient } from "@tanstack/react-query";

import { tooltipContent } from "../utils/constants";

import { getLocationFromProvider } from "../utils/addressUtils";
import { resolveVqqLocationPart } from "../utils/vqqNameFormat";
import { isDuringMaintenance } from "../utils/maintenance";
import { findRfiColorMeta } from "../utils/serviceCategoriesColors";
import { DEFAULT_RFI_BADGE_STYLE } from "../utils/rfiCatalogServiceNames";
import SingleDocumentScopeBadge, {
  DefaultFoundationScopeBadge,
} from "./SingleDocumentScopeBadge";
import DocumentLibraryActivityLog from "./LibraryRequests/DocumentLibraryActivityLog";

export default function Drawer({
  docType,

  docId,

  documents,

  fallbackDocument,

  drawerLoading,

  drawerIsSuccess,

  refetch,

  libraryRefetech,

  scheduleLibraryRefresh,
}) {
  const dispatch = useDispatch();
  // Use list item as fallback when API returns empty (e.g. Backend User / local)
  const providerName = documents?.provider_name ?? fallbackDocument?.provider_name ?? "";
  const documentLocation = documents?.document_location ?? fallbackDocument?.document_location ?? "";
  const documentStatus = documents?.document_status ?? fallbackDocument?.document_status;
  const userRole = useUserRole();
  // Kept for activity-log fallback only (comment renders inside the archive entry).
  const archiveComment =
    documents?.archive_comment ?? fallbackDocument?.archive_comment ?? "";
  const archivedBy =
    documents?.archived_by ?? fallbackDocument?.archived_by ?? "";
  const archivedOn =
    documents?.archived_on ?? fallbackDocument?.archived_on ?? null;
  const { data: drawerActivity, isLoading: drawerActivityLoading } =
    useGetDocumentActivity(
      docId,
      isDiligentAdminUserRole(userRole) && Boolean(docId)
    );
  const requestedOn = documents?.requested_on ?? fallbackDocument?.requested_on;
  const assessmentDate =
    documents?.assessment_date ?? fallbackDocument?.assessment_date;
  const entityType = documents?.document_type ?? documents?.entity_type ?? fallbackDocument?.entity_type;
  const isVqaDocument = entityType?.toUpperCase?.() === "VQA";
  const entityKind = entityType?.toUpperCase?.();
  const capabilities = documents?.capabilities ?? fallbackDocument?.capabilities ?? [];
  const additionalScope =
    documents?.additional_scope ?? fallbackDocument?.additional_scope ?? [];

  const isDrawerOpen = useSelector((state) => state.layout.drawerOpen);

  const [bookmark, setBookmark] = useState(false);

  const [requestAccessStatus, setRequestAccessStatus] = useState(false);

  const [requestReleaseStatus, setReleaseAccessStatus] = useState(false);

  const [isSuccess, setIsSuccess] = useState(false);

  const navigate = useNavigate();

  const [showChevron, setShowChevron] = useState(false);

  const isLoggedInUser = useSelector((state) => state.loggedInUser.userDetails);
  const isVqaOnly = useIsOrgVqaOnly();

  const [showNotification, setShowNotification] = useState(false);

  const [showErrorNotification, setShowErrorNotification] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const lowerCaseDocType = docType?.toLowerCase();

  const { rfis } = useSelector((state) => state.rfisColors);

  const rfiScopeSingleBadgeStyle = (() => {
    const dn = documents?.document_name;
    if (!dn) return DEFAULT_RFI_BADGE_STYLE;
    const meta = findRfiColorMeta(dn, rfis);
    if (meta) {
      return {
        bgColor: meta.bg,
        textColor: meta.text,
        borderColor: meta.border,
      };
    }
    return DEFAULT_RFI_BADGE_STYLE;
  })();

  const [open, setOpen] = useState(false);

  const {
    mutate: bookmarkDocument,

    isError: bookMarkError,

    error: bookMarkErrorMessage,
  } = usePostDocumentsBookmarks();

  const {
    mutate: requestAccess,

    isError: requestIsError,

    error: requestErrorMessage,
  } = usePostRequestAccess();

  // Handle error notifications

  useEffect(() => {
    if (bookMarkError || requestIsError) {
      setShowNotification(true);
    } else {
      setShowNotification(false);
    }
  }, [requestIsError, bookMarkError]);

  useEffect(() => {
    if (documents) {
      setBookmark(documents?.is_bookmarked);

      setRequestAccessStatus(documents?.is_requested);

      setReleaseAccessStatus(documents?.is_released);
    }
  }, [documents, requestAccess, bookmarkDocument]);

  const handleBookmark = () => {
    bookmarkDocument(
      {
        document_id: documents?.document_id,
      },

      {
        onSuccess: () => {
          setBookmark(!bookmark);
        },
      }
    );
  };
  const { hcUserId } = useSelector((state) => state.userRights);
  const { mutateAsync: sendRfis } = useSendRfiToPurchaseVendor();
  const { isSubmitting, begin, end } = useExclusiveSubmit();

  const { data: purchaseVendorBySponsor } = useGetPurchaseVendorBySponsor(
    documents?.provider_id
  );

  const getDocumentLocation = (document, options = { short: false, abbvState: true }) => {
    return getLocationFromProvider({
      address: document?.document_address || document?.document_location,
      city: document?.document_city,
      state: document?.document_state,
      country: document?.document_country
    },
      options
    ) || document?.document_location || "";
  };

  const refreshDocumentLibrary = scheduleLibraryRefresh ?? libraryRefetech;

  const handleRequestAccess = async () => {
    if (documents?.document_type === "VQA") {
      requestAccess(docId, {
        onSuccess: () => {
          setIsSuccess(true);

          setRequestAccessStatus(true);

          setShowNotification(true);

          refreshDocumentLibrary?.();
        },

        onError: () => {
          setIsSuccess(false);

          setOpen(false);
        },
      });
    }

    if (documents?.document_type === "RFI") {
      const todaysDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
      let uniqueName;
      // The locator has to be the same "City, State" the app shows on the VQQ, otherwise
      // HyperComply and D360 disagree on the name.
      const locationPart =
        resolveVqqLocationPart(documents) ||
        getDocumentLocation(documents, { short: true, abbvState: true });
      uniqueName = `Diligent - ${documents?.document_name} - ${todaysDate
        .toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        })
        .toUpperCase()} - ${locationPart}`;

      // Prepare the request payload
      const rfiRequest = {
        emails: [documents?.vendor_contact_email || ""],
        name: uniqueName,
        owner_id: hcUserId,
        template_id: documents?.hc_template_id,
        due_by: todaysDate,
        invite_message: "",
        send_questionnaire: true,
        vendor_name: providerName || "",
        template_name: documents?.document_name || "",
        provider_id: documents.provider_id || "",
        location: documents?.document_location || "",
        location_city: documents?.document_city || "",
        location_state: documents?.document_state || "",
        location_country: documents?.document_country || "",
        is_global_template: documents?.is_global_template || false,
        // TODO: update address, city, state, country
      };

      if (!begin()) return;

      try {
        await sendRfis({
          id: purchaseVendorBySponsor?.hc_purchase_vendor_id,
          data: rfiRequest,
        });
        setTimeout(() => {
          requestAccess(docId, {
            onSuccess: () => {
              setIsSuccess(true);

              setRequestAccessStatus(true);

              setShowNotification(true);

              refreshDocumentLibrary?.();
            },
          });
        }, 1000);
      } catch (error) {
        setErrorMessage(
          error?.response?.data?.message ||
            "Failed to send VQQ request. Please try again."
        );
        setShowErrorNotification(true);
        setOpen(false);
      } finally {
        end();
      }
    }
  };

  const {
    mutate: getFile,

    data: files,

    isError: isFileError,

    error: errorMessageFile,
  } = useDocumentFile();

  const fileDownload = () => {
    getFile(documents?.download_document_id ?? documents?.document_id);
  };

  const navigateToMyDocs = () => {
    navigate("/MyDocuments");

    dispatch(setDrawer(false));
  };

  useEffect(() => {
    if (isFileError) {
      return;
    }

    if (files?.download_link) {
      // Fetch the file and trigger download

      fetch(files.download_link)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }

          return response.blob(); // Convert the response to a Blob
        })

        .then((blob) => {
          const url = URL.createObjectURL(blob); // Create an Object URL

          const a = document.createElement("a");

          a.style.display = "none"; // Hide the anchor element

          a.href = url;

          a.download = "file.pdf"; // Suggested file name for download

          document.body.appendChild(a);

          a.click(); // Programmatically trigger the click

          document.body.removeChild(a); // Clean up

          URL.revokeObjectURL(url); // Release the Object URL
        })

        .catch((error) => {
          console.error("Failed to download file:", error);
        });
    }
  }, [files, isFileError]);

  const openModal = () => {
    setOpen(true);
  };

  const hasDownloadRights = isLoggedInUser?.rights?.includes("Download RFIs");

  const hasRequestAccessRights = isLoggedInUser?.rights?.includes(
    "Request Audit Reports"
  );

  const auditRights = isLoggedInUser?.rights?.includes("Download Audit Reports");

  const isMaintenanceDisabled = docType === "RFI" && isDuringMaintenance();
  const offersRequestAccess =
    canRequestLibraryDocAccess(userRole) && canRequestLibraryDocument(docType, isVqaOnly);

  return (
    <Dialog
      open={isDrawerOpen}
      onClose={() => {
        dispatch(setDrawer(false));
      }}
      className="relative z-10"
    >
      <DialogBackdrop className="fixed inset-0 bg-transparent bg-opacity-40 backdrop-blur-sm backdrop-filter transition-opacity duration-500 ease-in-out" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel className="pointer-events-auto relative w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700">
              <>
                <TransitionChild>
                  <div className="absolute left-0 top-0 -ml-8 flex pr-2 pt-4 duration-500 ease-in-out data-[closed]:opacity-0 sm:-ml-10 sm:pr-4">
                    <button
                      type="button"
                      onClick={() => {
                        dispatch(setDrawer(false));
                      }}
                      className="relative rounded-md text-black-300"
                    >
                      <span className="absolute -inset-2.5" />

                      <span className="sr-only">Close panel</span>

                      <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                    </button>
                  </div>
                </TransitionChild>

                <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl w-11/12 md:w-full">
                  {drawerLoading ? (
                    <DrawerSkeleton />
                  ) : (
                    <>
                      <div className="bg-[#1B0E4C] px-4 sm:px-6 w-11/12 m-auto rounded-lg max-h-60 pb-7">
                        <DialogTitle className="text-base font-semibold leading-6 text-gray-900 relative">
                          <div className="w-[19rem] md:w-[21rem] h-[9.1rem] border-2 border-[#7543D0] border-b-0 mt-5 absolute ml-2"></div>

                          <div
                            className="flex items-center justify-end mt-4 cursor-pointer relative z-10"
                            onClick={handleBookmark}
                          >
                            {/* {bookmark ? (
                              <FilledBookmark
                                className="w-8 h-8 text-white mt-1"
                                style={{ marginRight: "-6px" }}
                              />
                            ) : (
                              <BookmarkIcon
                                className="w-8 h-8 text-white mt-1"
                                style={{ marginRight: "-6px" }}
                              />
                            )} */}
                            <span
                              className="w-8 h-8 text-white mt-1"
                              style={{ marginRight: "-6px" }}
                            ></span>
                          </div>

                          <div className="flex justify-between items-center text-white mt-12 ml-6">
                            <div>
                              <h1 className="text-3xl font-semibold tracking-wide">
                                {providerName}
                              </h1>

                              <p className="text-xs mt-2">{docType}</p>
                            </div>

                            <div className="text-orange-500 text-2xl mr-6">
                              |
                            </div>
                          </div>
                        </DialogTitle>
                      </div>

                      <div className="relative mt-6 flex-1 px-4 sm:px-6">
                        <div className="mb-3 ml-1 flex justify-between items-center">
                          <h3 className="text-lg font-semibold">
                            {documents?.document_type === "RFI" &&
                              documents?.document_name}
                          </h3>{" "}
                          <dd
                            style={{
                              backgroundColor:
                                (entityType?.toLowerCase?.() ?? "") === "rfi"
                                  ? "rgb(79, 70, 229)"
                                  : "rgb(225, 29, 72)",
                            }}
                            className="inline-flex cursor-default items-center gap-x-3 rounded-md text-white py-1 px-2 text-sm font-semibold shadow-sm bg-[#E11D48]"
                          >
                            <p className="flex-1 text-center">
                              {entityType === "VQA"
                                ? "Audit"
                                : entityType ?? "—"}
                            </p>

                            {(entityType?.toLowerCase?.() ?? "") === "rfi" ? (
                              <CheckCircleIcon className="h-5 w-5" />
                            ) : (
                              <CheckBadgeIcon className="h-5 w-5" />
                            )}
                          </dd>
                        </div>

                        <div
                          className="flex items-center justify-between space-x-5"
                          style={{
                            display:
                              documents?.document_status === "Rejected"
                                ? "none"
                                : "flex",
                          }}
                        >
                          {/* <CustomButton
                            color={bookmark ? "bg-gray-200" : "bg-white"}
                            size="text-base"
                            weight="font-normal"
                            height="h-9"
                            width="w-48"
                            textColor="text-black"
                            text={bookmark ? "Bookmarked" : "Bookmark"}
                            border="border-2 border-gray-100"
                            icon={
                              bookmark && (
                                <FilledBookmark className="w-5 h-5 text-gray-500" />
                              )
                            }
                            disabled={documents?.document_status === "Upcoming"}
                            onClick={handleBookmark}
                          /> */}

                          {/* Replace the conditional button with three separate buttons */}

                          {!(documents?.document_status === "Pending") && (
                            <>
                              {/* Request Access Button */}

                              {(documents?.document_status === "Published" ||
                                // documents?.document_status === "Upcoming" ||
                                documents?.document_status === "Cancelled") &&
                                offersRequestAccess && (
                                <CustomButton
                                  color={
                                    !hasRequestAccessRights || isMaintenanceDisabled
                                      ? "bg-gray-300"
                                      : "bg-indigo-600"
                                  }
                                  size="text-base"
                                  weight="font-normal"
                                  height="h-9"
                                  width="w-48"
                                  textColor="text-white"
                                  text="Request Access"
                                  onClick={openModal}
                                  disabled={!hasRequestAccessRights || isMaintenanceDisabled}
                                />
                              )}

                              {/* 
                              {documents?.document_status === "Released" && (
                                <CustomButton
                                  color={
                                    !auditRights
                                      ? "bg-gray-300"

                                      : "bg-indigo-600"
                                  }
                                  size="text-base"
                                  weight="font-normal"
                                  height="h-9"
                                  width="w-48"
                                  textColor="text-white"
                                  text="Download"
                                  onClick={fileDownload}
                                  disabled={!auditRights}
                                />
                              )} */}

                              {/* Rejected Button */}

                              {/* {documents?.document_status === "Rejected" && (
                                <CustomButton
                                  color="bg-gray-300"
                                  size="text-base"
                                  weight="font-normal"
                                  height="h-9"
                                  width="w-48"
                                  textColor="text-white"
                                  text="Rejected"
                                  disabled={true}
                                />
                              )} */}

                              {/* View Details Button (for other statuses) */}
                              {docType === "RFI" &&
                                !isVqaOnly &&
                                documents?.hc_questionnaire_id &&
                                documents?.document_status === "Released" && (
                                  <CustomButton
                                    color="bg-indigo-600"
                                    size="text-base"
                                    weight="font-normal"
                                    height="h-9"
                                    width="w-48"
                                    textColor="text-white"
                                    text="View Details"
                                    onClick={() => {
                                      navigate(
                                        `/RFI/${documents.hc_questionnaire_id}/Details`
                                      );
                                      dispatch(setDrawer(false));
                                    }}
                                  />
                                )}
                              {/* {documents?.document_status !== "Published" &&
                                documents?.document_status !== "Released" &&
                                documents?.document_status !== "Rejected" &&
                                documents?.document_status !== "Upcoming" &&
                                documents?.document_status !== "Pending" && (
                                  <CustomButton
                                    color="bg-indigo-600"
                                    size="text-base"
                                    weight="font-normal"
                                    height="h-9"
                                    width="w-48"
                                    textColor="text-white"
                                    text="View Details"
                                    onClick={navigateToMyDocs}
                                  />
                                )} */}
                            </>
                          )}
                        </div>

                        <div className="px-1.5 mt-3">
                          <div className="border-y border-gray-100 py-3 flex justify-between">
                            <h4 className="font-semibold text-sm text-gray-500">
                              Vendor
                            </h4>

                            <p className="text-sm">
                              {providerName}
                            </p>
                          </div>

                          <div className="border-b border-gray-100 py-3 flex justify-between">
                            <h4 className="font-semibold text-sm text-gray-500">
                              Status
                            </h4>

                            <p className="text-sm font-semibold">
                              <StatusBadges
                                statusName={documentStatus}
                              />
                            </p>
                          </div>

                          <div className="border-b border-gray-100 py-3 flex justify-between gap-8">
                            <h4 className="font-semibold text-sm text-gray-500">
                              Location
                            </h4>

                            <p className="text-sm text-right">
                              <div>
                                {(() => {
                                    return getDocumentLocation(documents);
                                })()}
                              </div>
                            </p>
                          </div>

                          {(isVqaDocument ||
                            documents?.published_on != null ||
                            requestedOn != null) && (
                              <div
                                className="border-b border-gray-100 py-3 flex justify-between"
                                style={{
                                  display:
                                    (!isVqaDocument &&
                                      documents?.published_on == null &&
                                      requestedOn == null) ||
                                    documentStatus === "Rejected"
                                      ? "none"
                                      : "flex",
                                }}
                              >
                                <h4 className="font-semibold text-sm text-gray-500">
                                  {isVqaDocument
                                    ? "Assessment Date"
                                    : documentStatus === "Released" ||
                                      documentStatus === "Published"
                                    ? documents?.published_on != null && "Publish Date"
                                    : requestedOn != null && "Request Date"}
                                </h4>

                                <p className="text-sm">
                                  {isVqaDocument
                                    ? assessmentDate != null
                                      ? dateFormat(assessmentDate)
                                      : "TBD"
                                    : documentStatus === "Released" ||
                                      documentStatus === "Published"
                                    ? documents?.published_on != null &&
                                      dateFormat(documents?.published_on)
                                    : requestedOn != null &&
                                      dateFormat(requestedOn)}
                                </p>
                              </div>
                            )}

                          {/* {(documents?.capa_response_date !== null &&

                            documents?.document_status === "Published") ||

                            (documents?.document_status === "Released" && (

                              <div className="border-b border-gray-100 py-3 flex justify-between">

                                <h4 className="font-semibold text-sm text-gray-500">

                                  CAPA Response Date

                                </h4>

                                <p className="text-sm">

                                  {documents?.capa_response_date !== null &&

                                    dateFormat(documents?.capa_response_date)}

                                </p>

                              </div>

                            ))} */}

                          {documents?.document_status === "Rejected" &&
                            documents?.rejected_reason?.length > 0 && (
                              <div className="border-b border-gray-100 py-3">
                                <h4 className="font-semibold text-sm text-gray-500">
                                  Reason for Rejection
                                </h4>

                                <p className="text-sm whitespace-break-spaces mt-3">
                                  {documents?.rejected_reason}
                                </p>
                              </div>
                            )}

                          {isDiligentAdminUserRole(userRole) ? (
                            <DocumentLibraryActivityLog
                              items={drawerActivity?.items}
                              activityNavigation={drawerActivity}
                              loading={drawerActivityLoading}
                              entityType={entityType}
                              entityId={
                                documents?.entity_id ?? fallbackDocument?.entity_id
                              }
                              hcQuestionnaireId={
                                documents?.hc_questionnaire_id ??
                                fallbackDocument?.hc_questionnaire_id
                              }
                              vqaRequestId={
                                documents?.vqa_request_id ??
                                fallbackDocument?.vqa_request_id
                              }
                              providerName={providerName}
                              documentStatus={documentStatus}
                              archiveComment={archiveComment}
                              archivedBy={archivedBy}
                              archivedOn={archivedOn}
                              closeDrawerOnNavigate
                              defaultOpen
                              showTopBorder={false}
                            />
                          ) : null}

                          {(entityKind === "VQA" || entityKind === "RFI") && (
                            <div className="border-b border-gray-100 py-3">
                              <h4 className="font-semibold text-sm text-gray-500">
                                Scope
                              </h4>

                              <div className="mt-3">
                                {entityKind === "VQA" && (
                                  <>
                                    {capabilities?.length > 0 || additionalScope?.length > 0 ? (
                                      <ScopeWithAdditionalTags
                                        services={capabilities}
                                        additionalScope={additionalScope}
                                        capabilitiesProps={{
                                          icon: true,
                                          showLimited: false,
                                        }}
                                      />
                                    ) : (
                                      <DefaultFoundationScopeBadge
                                        tooltipContent={tooltipContent}
                                      />
                                    )}
                                  </>
                                )}
                                {entityKind === "RFI" && (
                                  <>
                                    {capabilities?.length > 0 ? (
                                      <Capabilities
                                        services={capabilities}
                                        icon={true}
                                        showLimited={false}
                                        applyRfiCatalogCustomLabels={true}
                                      />
                                    ) : (
                                      <SingleDocumentScopeBadge
                                        documentName={documents?.document_name}
                                        badgeStyle={rfiScopeSingleBadgeStyle}
                                        tooltipContent={tooltipContent}
                                        roundedClassName="rounded"
                                      />
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            </DialogPanel>
          </div>
        </div>
      </div>

      <Modal
        open={open}
        setOpen={setOpen}
        onClick={requestAccessStatus ? navigateToMyDocs : handleRequestAccess}
        is_requested={requestAccessStatus}
        title={
          requestAccessStatus
            ? "Document request successful"
            : "Confirm Document Request"
        }
        loading={isSubmitting}
        textBtn={isSubmitting ? "Requesting..." : "Request Access"}
        description={
          requestAccessStatus
            ? "The request for this document has been successful. View all requests by clicking the My Documents button below."
            : "Confirm that you would like to request this document from the vendor."
        }
      />

      <Notification
        show={showErrorNotification}
        setShow={setShowErrorNotification}
        errorMessage={errorMessage}
        status="error"
      />
    </Dialog>
  );
}
